import { zodResolver } from '@hookform/resolvers/zod';
import { Autocomplete, CircularProgress, Stack, TextField, Typography } from '@mui/material';
import { UpdateLineItemRequestSchema } from '@vy/api-definition';
import { INTEGRATION_PROVIDERS, type LineItem } from '@vy/types';
import { isNotNullOrUndefined } from '@vy/utils';
import { useEffect, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useQueryClient } from 'react-query';
import { VyDialog } from '../../../../../components/new-design/feedback/VyDialog';
import { useInvoiceProductsIntegrations } from '../../../../../hooks/requests/use-invoice-products-integrations';
import { useUpdateLineItem } from '../../../../../hooks/requests/use-update-line-item';
import { useActivatedIntegration } from '../../../../../hooks/use-activated-integration';
import { useFormattedXeroProducts } from '../../../../../hooks/use-formatted-xero-products';
import { getPrivateApi } from '../../../../../services/api';
import { getDirtyValues } from '../../../../../services/form-utils';
import { queryKeys } from '../../../../../services/query-key/query-keys';
import { runMutation } from '../../../../../utils/vy-mutation';
import type { FormattedIntegrationProduct } from '../../../../../utils/types/formatted-integration-product';
import {
  LINE_ITEM_FIELD_CONFIG,
  SharedLineItemFormContent,
} from '../../dynamic-invoice-view-components/dynamic-invoice-product-line-item/shared-line-item-components';
import type { ModifyLineItemData } from '../types';

type SharedUsageLineItemDialogProps = {
  open: boolean;
  handleClose: () => void;
  lineItemId: string;
  lineItemData: ModifyLineItemData;
  invoiceId: string;
};

const buildFormValues = (data: ModifyLineItemData, lineItemId: string) => ({
  id: lineItemId,
  type: data.type,
  modifiedName: data.modifiedName ?? data.name,
  modifiedDescription: data.modifiedDescription ?? data.description,
  modifiedPrice: data.modifiedPrice ?? data.price,
});

export function SharedUsageLineItemDialog({
  open,
  handleClose,
  lineItemId,
  lineItemData,
  invoiceId,
}: SharedUsageLineItemDialogProps) {
  const queryClient = useQueryClient();
  const updateLineItemMutation = useUpdateLineItem();

  const methods = useForm({
    resolver: zodResolver(UpdateLineItemRequestSchema),
    defaultValues: buildFormValues(lineItemData, lineItemId),
  });

  useEffect(() => {
    const values = buildFormValues(lineItemData, lineItemId);
    methods.reset(values);
  }, [lineItemData, lineItemId, methods]);

  const isXeroEnabled = useActivatedIntegration(INTEGRATION_PROVIDERS.xero);

  const [selectedXeroProduct, setSelectedXeroProduct] = useState<FormattedIntegrationProduct | null>(null);
  const initialXeroProductIdRef = useRef<string | undefined>(undefined);

  const { products: formattedXeroProducts, isLoading: isLoadingXeroProducts, setSearchInput } =
    useFormattedXeroProducts();

  const { data: invoiceProductsIntegrations } = useInvoiceProductsIntegrations(
    invoiceId,
    INTEGRATION_PROVIDERS.xero,
    { enabled: isXeroEnabled, suspense: false },
  );

  useEffect(() => {
    if (invoiceProductsIntegrations) {
      const mapping = invoiceProductsIntegrations.products.find((p) => p.id === lineItemId);

      if (isNotNullOrUndefined(mapping?.externalId)) {
        setSelectedXeroProduct({
          id: mapping.externalId,
          name: mapping.externalName ?? mapping.externalId,
        });
        initialXeroProductIdRef.current = mapping.externalId;
      }
    }
  }, [invoiceProductsIntegrations, lineItemId]);

  const onSubmit = methods.handleSubmit(async (data) => {
    const dirtyFields = methods.formState.dirtyFields;
    const dirtyValues = getDirtyValues(dirtyFields, data);

    const updatePayload = {
      id: lineItemId,
      ...dirtyValues,
      type: lineItemData.type,
    };

    await runMutation(
      updateLineItemMutation,
      {
        lineItemId,
        updateLineItemRequest: updatePayload,
      },
      {
        pending: 'Updating line item...',
        success: 'Line item updated',
        error: 'Failed to update line item',
      },
      {
        onSuccess: async () => {
          if (isXeroEnabled && selectedXeroProduct?.id !== initialXeroProductIdRef.current) {
            const api = getPrivateApi();
            await api.modifyExternalProduct({
              productId: lineItemId,
              integration: INTEGRATION_PROVIDERS.xero,
              externalId: selectedXeroProduct?.id ?? undefined,
            });
          }
          await queryClient.invalidateQueries(queryKeys.dynamicInvoice(invoiceId));
        },
      },
    );
    handleClose();
  });

  // Build a minimal LineItem-like object for SharedLineItemFormContent
  // The component only uses 'id' and 'modifiedPrice' fields
  const mockLineItem = {
    id: lineItemId,
    type: lineItemData.type,
    modifiedName: lineItemData.modifiedName,
    modifiedDescription: lineItemData.modifiedDescription,
    modifiedPrice: lineItemData.modifiedPrice,
  } as unknown as LineItem;

  return (
    <VyDialog
      open={open}
      onClose={handleClose}
      label={`Update ${lineItemData.type.toLowerCase()} line item`}
      labelDescription={`Update the ${lineItemData.type.toLowerCase()} line item details`}
      cancelButtonText="Cancel"
      ctaText="Update"
      ctaDisabled={updateLineItemMutation.isLoading}
      onSubmit={onSubmit}
    >
      <FormProvider {...methods}>
        <SharedLineItemFormContent
          fieldConfig={LINE_ITEM_FIELD_CONFIG}
          lineItem={mockLineItem}
          onUndoSuccess={handleClose}
        />
      </FormProvider>
      {isXeroEnabled && (
        <Stack
          sx={{
            gap: 1,
            padding: "10px",
            paddingTop: 0
          }}>
          <Typography
            sx={{
              color: "text.secondary",
              fontSize: 13
            }}>
            ERP Settings
          </Typography>
          <Autocomplete
            options={formattedXeroProducts}
            loading={isLoadingXeroProducts}
            getOptionLabel={(option) => option.name}
            getOptionKey={(option) => option.id}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            value={selectedXeroProduct}
            onChange={(_, value) => setSelectedXeroProduct(value)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Xero ERP ID"
                placeholder="Select Xero Product"
                size="medium"
                slotProps={{
                  ...params.slotProps,
                  inputLabel: { shrink: true },

                  input: {
                    ...params.slotProps.input,
                    endAdornment: (
                      <>
                        {isLoadingXeroProducts ? <CircularProgress color="inherit" size={16} /> : null}
                        {params.slotProps.input.endAdornment}
                      </>
                    ),
                  }
                }}
                onChange={(e) => {
                  setSearchInput(e.target.value || undefined);
                }}
              />
            )}
          />
        </Stack>
      )}
    </VyDialog>
  );
}
